import { useSelector } from 'react-redux';
import {
  getIsNodeEditorReadOnly,
  getIsYesNoSplitDataToggleEnabled,
  getStartNodeTriggerNodeClass,
} from 'modules/workflows/ducks';
import { HelpToggletip, TopTooltip } from 'ui/tooltip';
import {
  isStartTileUnfilterable,
  isUndeterminableOrigin,
  FIELDS_MATCH_RADIO_OPTIONS,
} from 'modules/workflowNodeEditors/components/YesNoSplitEditor';
import { Description } from 'modules/workflowNodeEditors/components/YesNoSplitEditor/YesNoSplitToggle';
import { StyledRadioList } from './FieldsMatchEditor.styles';
import { LOCAL_MATCH_TRIGGERING_EVENT_FILTERS } from './constants';

const getRadioOptions = ({
  startNodeClass = '',
  isReadOnly = false,
  isMatchTriggeringEventSelected = false,
  isYesNoSplitDataToggleEnabled = false,
}: {
  startNodeClass: string;
  isReadOnly: boolean;
  isMatchTriggeringEventSelected: boolean;
  isYesNoSplitDataToggleEnabled: boolean;
}) => {
  const [userOption, eventOption] = FIELDS_MATCH_RADIO_OPTIONS;
  const isUnfilterable = isStartTileUnfilterable(startNodeClass);
  const isUndeterminable = isUndeterminableOrigin(startNodeClass);

  if (!isYesNoSplitDataToggleEnabled) {
    return LOCAL_MATCH_TRIGGERING_EVENT_FILTERS.map((option) => ({
      ...option,
      isDisabled: isReadOnly,
    }));
  }

  return [
    { ...userOption, isDisabled: isReadOnly },
    {
      ...eventOption,
      isDisabled: isReadOnly || isUnfilterable,
      ...(!isUnfilterable && {
        description: <Description isAllEventsSelected={!isMatchTriggeringEventSelected} />,
      }),
      name: () => (
        <TopTooltip
          isEnabled={isUnfilterable}
          tooltipContent={'This journey’s entry source is not an event.'}
        >
          {eventOption.name}
          {isUndeterminable && isMatchTriggeringEventSelected && (
            <HelpToggletip
              iconProps={{ height: 20, width: 20 }}
              tooltipContent={
                'Since this journey’s entry source is "Other journey", the specific triggering event(s) can’t be shown here.'
              }
            />
          )}
        </TopTooltip>
      ),
    },
  ];
};

export const FieldsMatchRadio = ({
  isMatchTriggeringEventSelected,
  onRadioUpdate,
  radioValue,
}: {
  isMatchTriggeringEventSelected: boolean;
  onRadioUpdate: () => void;
  radioValue: string;
}) => {
  const startNodeClass = useSelector(getStartNodeTriggerNodeClass);
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);
  const isYesNoSplitDataToggleEnabled = useSelector(getIsYesNoSplitDataToggleEnabled);

  return (
    <StyledRadioList
      label="Filter by"
      name="local-match-triggering-event-fields"
      onSelect={onRadioUpdate}
      options={getRadioOptions({
        startNodeClass,
        isReadOnly: isTileEditorReadOnly,
        isMatchTriggeringEventSelected,
        isYesNoSplitDataToggleEnabled,
      })}
      value={radioValue}
    />
  );
};
